import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  Info,
  KeyRound,
  LockKeyhole,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { api } from '../services/api';
import { UserRole } from '../types';

type TutorialStep = {
  eyebrow: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  points: string[];
};

const tutorialSteps: TutorialStep[] = [
  {
    eyebrow: '01 · Acesso individual',
    title: 'Cada pessoa usa o próprio acesso',
    description: 'O login identifica quem está trabalhando e mantém a operação organizada. Nunca é necessário compartilhar a senha do dono com a equipe.',
    icon: UserRound,
    points: [
      'O dono entra com o próprio usuário e mantém o controle da empresa.',
      'Cada funcionário deve usar seu perfil, sem dividir usuário ou senha.',
      'Assim o FlowTab consegue mostrar quem realizou cada ação importante.',
    ],
  },
  {
    eyebrow: '02 · Perfis',
    title: 'Cada perfil vê somente o que precisa',
    description: 'Os perfis deixam a rotina mais simples e reduzem o risco de alguém alterar uma configuração sem autorização.',
    icon: Eye,
    points: [
      'Dono: acessa a operação, configurações da empresa, gerência e informações administrativas.',
      'Caixa: trabalha no PDV e nas áreas operacionais liberadas, sem precisar do login do dono.',
      'Garçom: usa o fluxo de atendimento e lançamento de pedidos, sem acesso às áreas administrativas.',
    ],
  },
  {
    eyebrow: '03 · Segunda proteção',
    title: 'A senha de gerente autoriza ações sensíveis',
    description: 'A senha interna não substitui o login. Ela funciona como uma confirmação extra quando uma ação pode causar impacto financeiro ou operacional.',
    icon: LockKeyhole,
    points: [
      'O funcionário continua trabalhando com o próprio perfil normalmente.',
      'Quando necessário, o dono ou gerente autorizado confirma a ação com sua senha.',
      'O funcionário não precisa descobrir nem receber a senha principal da empresa.',
    ],
  },
  {
    eyebrow: '04 · Cadastro interno',
    title: 'O dono cadastra toda a equipe',
    description: 'O funcionário não cria conta sozinho. O dono abre Configurações, cria o acesso e define exatamente o que aquela pessoa pode usar.',
    icon: UserRound,
    points: [
      'Na seção Equipe e permissões, informe nome, e-mail individual e uma senha inicial.',
      'Escolha um perfil pronto: Funcionário, Gerente ou Balcão.',
      'Ajuste individualmente as permissões de PDV, Produtos, Estoque, Relatórios, Configurações, Cozinha e Delivery.',
      'Depois do cadastro, a pessoa entra pelo login inicial usando o e-mail e a senha que o dono criou.',
    ],
  },
  {
    eyebrow: '05 · PIN de autorização',
    title: 'A senha de gerente é separada do login',
    description: 'Use a seção Acesso da Gerência para cadastrar até duas pessoas autorizadas a confirmar ações críticas. Esse PIN não cria uma conta de funcionário.',
    icon: KeyRound,
    points: [
      'O dono continua com seu login principal e pode cadastrar ou remover esses PINs.',
      'A senha do gerente autoriza uma ação específica, como uma correção ou alteração sensível.',
      'Nunca use o PIN como senha de todos os funcionários nem o deixe anotado no caixa.',
      'Se a pessoa sair da equipe, remova o PIN e o login dela nas Configurações.',
    ],
  },
  {
    eyebrow: '06 · Proteção na prática',
    title: 'O que acontece quando alguém tenta alterar algo?',
    description: 'O sistema mantém a operação rápida para tarefas comuns e pede confirmação somente quando a ação precisa de autoridade maior.',
    icon: ShieldCheck,
    points: [
      'Ações comuns, como lançar produtos e acompanhar pedidos, seguem sem interrupção desnecessária.',
      'Ações protegidas solicitam uma autorização antes de serem concluídas.',
      'A autorização fica registrada no histórico para o dono conferir depois.',
      'Se alguém sair da equipe, o dono remove o acesso de gerência imediatamente nas Configurações.',
    ],
  },
  {
    eyebrow: '07 · Pronto para começar',
    title: 'Checklist de segurança da sua equipe',
    description: 'Faça esta configuração uma vez e deixe a rotina mais segura sem tornar o atendimento complicado.',
    icon: CheckCircle2,
    points: [
      'Crie ou confirme um login individual para cada funcionário.',
      'Defina o perfil correto: Caixa para PDV ou Garçom para atendimento.',
      'Cadastre somente as pessoas autorizadas na Acesso da Gerência.',
      'Teste uma ação protegida com o funcionário presente e a autorização do gerente.',
      'Ao terminar o turno, cada pessoa deve sair do próprio acesso.',
    ],
  },
];

export const AccessTutorial = () => {
  const { user } = useStore();
  const location = useLocation();
  const userId = user?.id;
  const userRole = user?.role;
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const storageKey = `flowtab-access-tutorial-v1:${userId}`;

  useEffect(() => {
    if (location.pathname !== '/settings') setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const openTutorial = () => {
      setStepIndex(0);
      setIsOpen(true);
    };

    window.addEventListener('flowtab:open-access-tutorial', openTutorial);
    return () => window.removeEventListener('flowtab:open-access-tutorial', openTutorial);
  }, []);

  if (!user || userRole === UserRole.SAAS_ADMIN || location.pathname !== '/settings' || !isOpen) return null;

  const step = tutorialSteps[stepIndex];
  const StepIcon = step.icon;
  const isLastStep = stepIndex === tutorialSteps.length - 1;

  const finishTutorial = () => {
    window.localStorage.setItem(storageKey, 'seen');
    void api.auth.completeAccessTutorial();
    setIsOpen(false);
    setStepIndex(0);
  };

  const goNext = () => {
    if (isLastStep) {
      finishTutorial();
      return;
    }
    setStepIndex(current => current + 1);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="access-tutorial-title">
      <div className="relative max-h-[min(760px,calc(100vh-2rem))] w-full max-w-3xl overflow-hidden rounded-[2rem] border border-white/20 bg-white shadow-2xl">
        <div className="relative overflow-hidden bg-slate-950 px-6 pb-7 pt-6 text-white sm:px-10 sm:pb-9 sm:pt-8">
          <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-brand-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-300/30">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-300">Guia FlowTab</p>
                <h2 className="mt-1 text-xl font-black sm:text-2xl">Como funciona o acesso seguro</h2>
              </div>
            </div>
            <button type="button" onClick={finishTutorial} aria-label="Fechar tutorial" className="rounded-xl p-2 text-slate-300 transition hover:bg-white/10 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="relative mt-7 flex items-center gap-2" aria-label={`Etapa ${stepIndex + 1} de ${tutorialSteps.length}`}>
            {tutorialSteps.map((item, index) => (
              <div key={item.eyebrow} className={`h-1.5 flex-1 rounded-full transition-colors ${index <= stepIndex ? 'bg-emerald-300' : 'bg-white/15'}`} />
            ))}
            <span className="ml-2 whitespace-nowrap text-xs font-bold text-slate-300">{stepIndex + 1}/{tutorialSteps.length}</span>
          </div>
        </div>

        <div className="max-h-[calc(100vh-18rem)] overflow-y-auto px-6 py-7 sm:px-10 sm:py-9">
          <div className="flex items-start gap-4">
            <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100 sm:flex">
              <StepIcon className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-brand-600">{step.eyebrow}</p>
              <h3 id="access-tutorial-title" className="mt-2 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">{step.title}</h3>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">{step.description}</p>
            </div>
          </div>

          <div className="mt-7 grid gap-3">
            {step.points.map(point => (
              <div key={point} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm leading-5 text-slate-700">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span>{point}</span>
              </div>
            ))}
          </div>

          {stepIndex === 1 && (
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { label: 'Dono', text: 'Controle completo', tone: 'bg-violet-50 text-violet-700 border-violet-100' },
                { label: 'Caixa', text: 'Rotina do PDV', tone: 'bg-cyan-50 text-cyan-700 border-cyan-100' },
                { label: 'Garçom', text: 'Pedidos e mesas', tone: 'bg-amber-50 text-amber-700 border-amber-100' },
              ].map(profile => (
                <div key={profile.label} className={`rounded-2xl border p-4 ${profile.tone}`}>
                  <p className="text-xs font-black uppercase tracking-wider">{profile.label}</p>
                  <p className="mt-1 text-sm font-bold">{profile.text}</p>
                </div>
              ))}
            </div>
          )}

          {stepIndex === 2 && (
            <div className="mt-5 flex items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm leading-5 text-blue-900">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
              <p><strong>Importante:</strong> a senha de gerente é uma autorização interna para a empresa. Ela não deve ser enviada por mensagem, anotada perto do caixa ou usada como login compartilhado.</p>
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <button type="button" onClick={finishTutorial} className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800">
              <Info className="h-4 w-4" /> Fechar
            </button>
            <div className="flex gap-2 sm:gap-3">
              <button type="button" onClick={() => setStepIndex(current => Math.max(0, current - 1))} disabled={stepIndex === 0} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
                <ArrowLeft className="h-4 w-4" /> Voltar
              </button>
              <button type="button" onClick={goNext} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-slate-950/15 transition hover:bg-slate-800">
                {isLastStep ? 'Concluir tutorial' : 'Próxima etapa'} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
